<script setup lang="ts">
  import AddDocumentsDialog from '@/components/app/cases/organize-documents/AddDocumentsDialog.vue'
  import { useDocumentOrganization } from '@/components/app/cases/organize-documents/composables/useDocumentOrganization'
  import DocumentGroup from '@/components/app/cases/organize-documents/DocumentGroup.vue'
  import OrganizeDocumentsHeader from '@/components/app/cases/organize-documents/OrganizeDocumentsHeader.vue'
  import { Button } from '@/components/ui/button'
  import { useToast } from '@/components/ui/toast'
  import UnsavedChangesDialog from '@/components/ui/UnsavedChangesDialog.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import type { Document, ImageproxyImagePreset } from '@/types/generated/types.gen'
  import { DocumentsService } from '@/types/generated/services.gen'
  import { Plus } from 'lucide-vue-next'
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import {
    onBeforeRouteLeave,
    useRouter,
    type NavigationGuardNext,
    type RouteLocationNormalized
  } from 'vue-router'

  const router = useRouter()
  const { toast } = useToast()
  const caseStore = useCaseStore()
  const { trackEvent } = useMetrics()

  const documents = ref<Document[]>([])
  const documentThumbnails = ref<Map<string, ImageproxyImagePreset[]>>(new Map())
  const isLoading = ref(true)
  const viewOpenedAt = ref<Date | null>(null)

  type PageItem =
    import('@/components/app/cases/organize-documents/composables/useDocumentOrganization').PageItem
  type DocumentGroupType =
    import('@/components/app/cases/organize-documents/composables/useDocumentOrganization').DocumentGroup

  const { t } = useTranslation({
    en: {
      backToDocuments: 'Back to all documents',
      pdfEditor: 'PDF editor',
      files: 'files',
      downloadAsPdf: 'Download as PDF',
      saveDocuments: 'Save documents',
      noDocuments: 'No documents',
      loadingDocuments: 'Loading documents...',
      pages: 'pages',
      preview: 'Preview',
      splitWithAI: 'Split with AI',
      merge: 'Merge...',
      selectAllPages: 'Select all pages',
      addNewDocument: 'Add New Document',
      documentName: 'Document Name',
      newDocument: 'New document',
      mergeWithAnotherDocument: 'Merge with another document',
      mergeSelectedPages: 'Merge selected pages',
      mergeWithDocumentAbove: 'Merge with document above',
      mergeWithDocumentBelow: 'Merge with document below',
      mergeWithSpecificGroup: 'Merge with specific group',
      mergeAllDocuments: 'Merge all documents',
      duplicate: 'Duplicate',
      delete: 'Delete',
      undo: 'Undo',
      copy: 'Copy',
      rotate: 'Rotate',
      moreOptions: 'More options',
      editDocumentName: 'Edit document name',
      splitDocument: 'Split document',
      addDocuments: 'Add documents',
      addDocumentsDescription:
        'Select existing documents or upload new ones to add to this document group.',
      noExistingDocuments: 'No existing documents available',
      untitled: 'Untitled',
      cancel: 'Cancel',
      addSelected: 'Add selected',
      noPreviewAvailable: 'No preview available',
      page: 'Page',
      error: 'Error',
      documentNotFound: 'Document not found',
      noCaseId: 'No case ID provided in route',
      noDocumentsOrCaseId: 'No documents or case ID provided',
      noDocumentsFetched: 'No documents could be fetched',
      success: 'Success',
      documentsLoaded: 'Loaded {count} documents',
      specificDocumentsLoaded: 'Loaded {count} of {total} selected documents',
      allDocumentsLoaded: 'Loaded all {count} documents from case',
      documentsAutoMerged: 'Automatically merged {count} documents into one',
      cannotSplit: 'Cannot Split',
      documentMustHavePages: 'Document must have at least 2 pages to split',
      cannotSplitLastPage: 'Cannot split at the last page - no pages would remain in the new group',
      documentSplit: 'Document Split',
      documentSplitGroups: 'Document split into 2 groups: "{name1}" and "{name2}"',
      noDocumentUrl: 'No document URL available for preview',
      documentNameUpdated: 'Document name updated successfully',
      failedToUpdateName: 'Failed to update document name',
      aiTitleGenerated: 'AI Title Generated',
      aiTitleGeneratedDescription: 'AI has generated a new title for this document',
      aiTitleError: 'AI Title Error',
      failedToGenerateTitle: 'Failed to generate AI title',
      aiSplitSuccess: 'AI Split Success',
      aiSplitDescription: 'Document split into {count} groups using AI',
      aiAnalysisComplete: 'AI Analysis Complete',
      aiAnalysisNoSplit: 'Document analyzed but no split was needed',
      aiSplitError: 'Failed to split document with AI',
      selectAtLeastPages: 'Select at least {count} pages to merge',
      pagesMerged: '{count} pages merged',
      documentsMerged: 'Documents merged successfully',
      documentsMergedInto: 'Document "{sourceName}" merged into "{targetName}"',
      mergedDocument: 'Merged Document',
      documentDuplicated: 'Document "{name}" duplicated successfully',
      documentDeleted: 'Document "{name}" deleted successfully',
      newDocumentName: 'Copy of {name}',
      documentCopyName: 'Copy of {name}',
      cannotMergeDocumentWithItself: 'Cannot merge document with itself',
      invalidPageIndex: 'Invalid page index for split operation',
      documentNotFoundOrNoPages: 'Document not found or has no pages',
      pageWithNumber: 'Page {number}',
      unknown: 'Unknown',
      noDocumentAbove: 'No document above to merge with',
      noDocumentBelow: 'No document below to merge with',
      needMultipleDocuments: 'Need at least 2 documents to merge',
      confirmMergeAll:
        'Are you sure you want to merge all documents? This action cannot be undone.',
      confirmDelete: 'Are you sure you want to delete this document?',
      info: 'Info',
      documentPickerWouldOpen: 'Document picker would open here',
      pdfGenerationDownload: 'PDF generation and download would happen here',
      documentsSaved: 'Documents saved successfully',
      failedToSaveDocuments: 'Failed to save documents',
      failedToFetchCaseDocuments: 'Failed to fetch case documents',
      fileUploaded: 'File Uploaded',
      fileUploadedSuccess: 'File "{name}" uploaded successfully.',
      selectDocumentsOrUpload: 'Please select at least one existing document or upload a new one.',
      noDocumentGroupSelected: 'No document group or page selected.',
      documentsAdded: 'Documents Added',
      pagesAddedToGroup: 'Added {count} pages to "{groupName}".',
      documentGroupsCreated: 'Created {count} document group(s) with {pages} pages.',
      renameWithAI: 'Rename with AI',
      rotateLeft: 'Rotate Left',
      rotateRight: 'Rotate Right',
      highContrast: 'High Contrast',
      autoDetectEdges: 'Auto Detect Edges',
      moveTo: 'Move To',
      moveToAnotherDocument: 'Move to Another Document',
      createNewDocument: 'Create New Document',
      deletePages: 'Delete Pages',
      documentCreated: 'Created new document with {count} pages',
      pagesMoved: 'Moved {count} pages to "{targetName}"',
      pagesDeleted: 'Deleted {count} pages: {pageNames}',
      pageDeleted: 'Page "{pageName}" deleted',
      pagesRotated: 'Rotated {count} pages {direction}',
      pageRotated: 'Page rotated {direction}',
      downloadPdf: 'Download PDF',
      downloadPage: 'Download Page',
      deletePage: 'Delete page',
      pageNotFound: 'Page not found',
      dropPagesHere: 'Drop pages here to add to "{documentName}"',
      savingChanges: 'Saving changes...',
      noChangesToSave: 'No changes to save',
      changesSavedSuccessfully: 'Changes saved successfully',
      failedToSaveChanges: 'Failed to save changes',
      generatingPDFs: 'Generating PDFs...',
      pdfGenerationFailed: 'PDF generation failed',
      resetChanges: 'Reset Changes',
      resetChangesConfirmation:
        'Are you sure you want to reset all changes? This action cannot be undone.',
      changesResetSuccessfully: 'Changes reset successfully',
      aiSplitRequiresOcr: 'AI split requires all documents to have completed OCR processing',
      downloadError: 'Download Error',
      changesNotSaved: 'Changes not saved',
      unsavedChangesDescription: 'Are you sure you want to leave without saving your changes?',
      discardChanges: 'Discard changes',
      goBack: 'Go back',
      downloadingOrganizedPdfs: 'Downloading organized PDFs...'
    },
    de: {
      backToDocuments: 'Zurück zu allen Dokumenten',
      pdfEditor: 'PDF-Editor',
      files: 'Dateien',
      downloadAsPdf: 'Als PDF herunterladen',
      saveDocuments: 'Dokumente speichern',
      noDocuments: 'Keine Dokumente',
      loadingDocuments: 'Dokumente werden geladen...',
      pages: 'Seiten',
      preview: 'Vorschau',
      splitWithAI: 'Mit AI teilen',
      merge: 'Zusammenführen...',
      selectAllPages: 'Alle Seiten auswählen',
      addNewDocument: 'Neues Dokument hinzufügen',
      documentName: 'Dokumentname',
      newDocument: 'Neues Dokument',
      mergeWithAnotherDocument: 'Mit einem anderen Dokument zusammenführen',
      mergeSelectedPages: 'Ausgewählte Seiten zusammenführen',
      mergeWithDocumentAbove: 'Mit dem Dokument darüber zusammenführen',
      mergeWithDocumentBelow: 'Mit dem Dokument darunter zusammenführen',
      mergeWithSpecificGroup: 'Mit einer bestimmten Gruppe zusammenführen',
      mergeAllDocuments: 'Alle Dokumente zusammenführen',
      duplicate: 'Duplizieren',
      delete: 'Löschen',
      undo: 'Rückgängig',
      copy: 'Kopieren',
      rotate: 'Drehen',
      moreOptions: 'Weitere Optionen',
      editDocumentName: 'Dokumentnamen bearbeiten',
      splitDocument: 'Dokument teilen',
      addDocuments: 'Dokumente hinzufügen',
      addDocumentsDescription:
        'Wählen Sie bestehende Dokumente aus oder laden Sie neue hoch, um sie zu dieser Dokumentengruppe hinzuzufügen.',
      noExistingDocuments: 'Keine bestehenden Dokumente verfügbar',
      untitled: 'Unbenannt',
      cancel: 'Abbrechen',
      addSelected: 'Ausgewählte hinzufügen',
      noPreviewAvailable: 'Keine Vorschau verfügbar',
      page: 'Seite',
      error: 'Fehler',
      documentNotFound: 'Dokument nicht gefunden',
      noCaseId: 'Keine Fall-ID in der Route bereitgestellt',
      noDocumentsOrCaseId: 'Keine Dokumente oder Fall-ID bereitgestellt',
      noDocumentsFetched: 'Es konnten keine Dokumente abgerufen werden',
      success: 'Erfolg',
      documentsLoaded: '{count} Dokumente geladen',
      specificDocumentsLoaded: '{count} von {total} ausgewählten Dokumenten geladen',
      allDocumentsLoaded: 'Alle {count} Dokumente aus dem Fall geladen',
      documentsAutoMerged: 'Automatisch {count} Dokumente zu einem zusammengeführt',
      cannotSplit: 'Kann nicht geteilt werden',
      documentMustHavePages: 'Dokument muss mindestens 2 Seiten haben, um geteilt zu werden',
      cannotSplitLastPage:
        'Kann nicht auf der letzten Seite teilen - es würden keine Seiten in der neuen Gruppe verbleiben',
      documentSplit: 'Dokument geteilt',
      documentSplitGroups: 'Dokument in 2 Gruppen geteilt: "{name1}" und "{name2}"',
      noDocumentUrl: 'Keine Dokumenten-URL für Vorschau verfügbar',
      documentNameUpdated: 'Dokumentname erfolgreich aktualisiert',
      failedToUpdateName: 'Fehler beim Aktualisieren des Dokumentnamens',
      aiTitleGenerated: 'AI-Titel generiert',
      aiTitleGeneratedDescription: 'AI hat einen neuen Titel für dieses Dokument generiert',
      aiTitleError: 'AI-Titel-Fehler',
      failedToGenerateTitle: 'Fehler beim Generieren des AI-Titels',
      selectAtLeastPages: 'Wählen Sie mindestens {count} Seiten zum Zusammenführen aus',
      pagesMerged: '{count} Seiten zusammengeführt',
      documentsMerged: 'Dokumente erfolgreich zusammengeführt',
      documentsMergedInto: 'Dokument "{sourceName}" in "{targetName}" zusammengeführt',
      mergedDocument: 'Zusammengeführtes Dokument',
      documentDuplicated: 'Dokument "{name}" erfolgreich dupliziert',
      documentDeleted: 'Dokument "{name}" erfolgreich gelöscht',
      newDocumentName: 'Kopie von {name}',
      documentCopyName: 'Kopie von {name}',
      cannotMergeDocumentWithItself: 'Kann Dokument nicht mit sich selbst zusammenführen',
      invalidPageIndex: 'Ungültiger Seitenindex für Teilungsvorgang',
      documentNotFoundOrNoPages: 'Dokument nicht gefunden oder hat keine Seiten',
      pageWithNumber: 'Seite {number}',
      unknown: 'Unbekannt',
      noDocumentAbove: 'Kein Dokument darüber zum Zusammenführen',
      noDocumentBelow: 'Kein Dokument darunter zum Zusammenführen',
      needMultipleDocuments: 'Mindestens 2 Dokumente zum Zusammenführen erforderlich',
      confirmMergeAll:
        'Sind Sie sicher, dass Sie alle Dokumente zusammenführen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      confirmDelete: 'Sind Sie sicher, dass Sie dieses Dokument löschen möchten?',
      info: 'Info',
      documentPickerWouldOpen: 'Dokumentenauswahl würde sich hier öffnen',
      pdfGenerationDownload: 'PDF-Generierung und Download würde hier stattfinden',
      documentsSaved: 'Dokumente erfolgreich gespeichert',
      failedToSaveDocuments: 'Fehler beim Speichern der Dokumente',
      failedToFetchCaseDocuments: 'Fehler beim Abrufen der Falldokumente',
      fileUploaded: 'Datei hochgeladen',
      fileUploadedSuccess: 'Datei "{name}" erfolgreich hochgeladen.',
      selectDocumentsOrUpload:
        'Bitte wählen Sie mindestens ein bestehendes Dokument aus oder laden Sie eine neue hoch.',
      noDocumentGroupSelected: 'Keine Dokumentengruppe oder Seite ausgewählt.',
      documentsAdded: 'Dokumente hinzugefügt',
      pagesAddedToGroup: '{count} Seiten zu "{groupName}" hinzugefügt.',
      documentGroupsCreated: '{count} Dokumentengruppe(n) mit {pages} Seiten erstellt.',
      renameWithAI: 'Mit AI umbenennen',
      rotateLeft: 'Nach links drehen',
      rotateRight: 'Nach rechts drehen',
      highContrast: 'Hoher Kontrast',
      autoDetectEdges: 'Kanten automatisch erkennen',
      moveTo: 'Verschieben nach',
      moveToAnotherDocument: 'Zu einem anderen Dokument verschieben',
      createNewDocument: 'Neues Dokument erstellen',
      deletePages: 'Seiten löschen',
      documentCreated: 'Neues Dokument mit {count} Seiten erstellt',
      pagesMoved: '{count} Seiten zu "{targetName}" verschoben',
      pagesDeleted: '{count} Seiten gelöscht: {pageNames}',
      pageDeleted: 'Seite "{pageName}" gelöscht',
      pagesRotated: '{count} Seiten {direction} gedreht',
      pageRotated: 'Seite {direction} gedreht',
      downloadPdf: 'PDF herunterladen',
      downloadPage: 'Seite herunterladen',
      deletePage: 'Seite löschen',
      pageNotFound: 'Seite nicht gefunden',
      dropPagesHere: 'Seiten hier ablegen, um sie zu "{documentName}" hinzuzufügen',
      savingChanges: 'Änderungen werden gespeichert...',
      noChangesToSave: 'Keine Änderungen zum Speichern',
      changesSavedSuccessfully: 'Änderungen erfolgreich gespeichert',
      failedToSaveChanges: 'Fehler beim Speichern der Änderungen',
      generatingPDFs: 'PDFs werden generiert...',
      pdfGenerationFailed: 'PDF-Generierung fehlgeschlagen',
      resetChanges: 'Änderungen zurücksetzen',
      resetChangesConfirmation:
        'Sind Sie sicher, dass Sie alle Änderungen zurücksetzen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      changesResetSuccessfully: 'Änderungen erfolgreich zurückgesetzt',
      downloadError: 'Download-Fehler',
      aiSplitSuccess: 'KI-Teilung erfolgreich',
      aiSplitDescription: 'Dokument mit KI in {count} Gruppen aufgeteilt',
      aiAnalysisComplete: 'KI-Analyse abgeschlossen',
      aiAnalysisNoSplit: 'Dokument analysiert, aber keine Teilung erforderlich',
      aiSplitError: 'Dokument konnte mit KI nicht geteilt werden',
      aiSplitRequiresOcr: 'KI-Teilung erfordert abgeschlossene OCR-Verarbeitung für alle Dokumente',
      changesNotSaved: 'Änderungen nicht gespeichert',
      unsavedChangesDescription: 'Sind Sie sicher, dass Sie ohne Speichern verlassen möchten?',
      discardChanges: 'Änderungen verwerfen',
      goBack: 'Zurück gehen',
      downloadingOrganizedPdfs: 'Organisierte PDFs werden heruntergeladen...'
    }
  })

  // Add the openAddDocumentsDialog function
  const openAddDocumentsDialog = (docIndex: number, pageIndex: number, side: 'left' | 'right') => {
    currentDocumentGroupIndex.value = docIndex
    currentPageIndex.value = pageIndex
    currentSide.value = side
    showAddDocuments.value = true
  }

  // Function to open add documents dialog from empty state
  const openEmptyStateAddDocuments = () => {
    currentDocumentGroupIndex.value = null
    currentPageIndex.value = null
    currentSide.value = 'right'
    showAddDocuments.value = true
  }

  const documentOrganization = useDocumentOrganization(t, {
    asProvider: true,
    additionalContext: {
      openAddDocumentsDialog,
      documentThumbnails
    }
  })

  const {
    organizedDocuments,
    originalDocumentState,
    hasChanges,
    caseId,
    allCaseDocuments,
    createDocumentPages,
    markAsChanged,
    isDragging,
    updateMousePosition,
    mergeAllDocuments
  } = documentOrganization

  // The hasChanges is automatically tracked by the useDocumentState composable
  // which is shared with the router navigation guard

  const showAddDocuments = ref(false)
  const currentDocumentGroupIndex = ref<number | null>(null)
  const currentPageIndex = ref<number | null>(null)
  const currentSide = ref<'left' | 'right'>('right')

  // Local dialog state for navigation guard
  const dialogState = {
    show: ref(false),
    onConfirm: ref<(() => void) | null>(null),
    onCancel: ref<(() => void) | null>(null)
  }

  // Store pending navigation info
  const pendingNavigation = ref<{
    to: RouteLocationNormalized
    next: NavigationGuardNext
  } | null>(null)

  // Flag to temporarily disable the guard
  const guardDisabled = ref(false)

  onBeforeRouteLeave(
    (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
      if (guardDisabled.value || !hasChanges.value) {
        return next()
      }

      pendingNavigation.value = { to, next }

      dialogState.onConfirm.value = () => {
        if (pendingNavigation.value) {
          const { to } = pendingNavigation.value
          pendingNavigation.value = null

          // Track changes discarded
          trackEvent(MetricEventType.PDF_ORGANIZE_CHANGES_DISCARDED, {
            case_uuid: caseId.value,
            document_count: organizedDocuments.value.length,
            had_changes: hasChanges.value
          })

          guardDisabled.value = true
          router
            .push(to.fullPath)
            .then(() => {
              guardDisabled.value = false
            })
            .catch(() => {
              guardDisabled.value = false
            })
        }
      }
      dialogState.onCancel.value = () => {
        pendingNavigation.value = null
      }
      dialogState.show.value = true

      return next(false)
    }
  )

  onMounted(async () => {
    viewOpenedAt.value = new Date()

    try {
      const caseIdFromRoute = router.currentRoute.value.params.caseId as string

      if (!caseIdFromRoute) {
        isLoading.value = false
        toast({
          title: t('error'),
          description: t('noCaseId'),
          variant: 'destructive'
        })
        return
      }

      caseId.value = caseIdFromRoute

      const documentIds = history.state?.documentIds || []

      await fetchAllCaseDocuments()

      // Track view opened
      trackEvent(MetricEventType.PDF_ORGANIZE_VIEW_OPENED, {
        case_uuid: caseIdFromRoute,
        document_count: documentIds.length || allCaseDocuments.value.length,
        specific_documents: documentIds.length > 0,
        document_uuids: documentIds
      })

      let validDocuments: Document[]

      if (documentIds.length > 0) {
        const documentPromises = documentIds.map(async (id: string) => {
          try {
            const response = await DocumentsService.documentsRetrieve({ uuid: id })
            return response
          } catch (error) {
            console.error(`Failed to fetch document ${id}:`, error)
            return null
          }
        })

        const fetchedDocuments = await Promise.all(documentPromises)
        validDocuments = fetchedDocuments.filter((doc) => doc !== null) as Document[]
      } else {
        validDocuments = allCaseDocuments.value || []
      }

      // Fetch thumbnails for all documents
      const thumbnailPromises = validDocuments.map(async (doc) => {
        try {
          const thumbnailResponse = await DocumentsService.documentsThumbnailsRetrieve({
            uuid: doc.uuid
          })
          return { uuid: doc.uuid, thumbnails: thumbnailResponse.thumbnails }
        } catch (error) {
          console.error(`Failed to fetch thumbnails for document ${doc.uuid}:`, error)
          return { uuid: doc.uuid, thumbnails: [] }
        }
      })

      const thumbnailResults = await Promise.all(thumbnailPromises)

      // Store thumbnails in the map
      thumbnailResults.forEach(({ uuid, thumbnails }) => {
        documentThumbnails.value.set(uuid, thumbnails)
      })

      if (validDocuments.length === 0) {
        isLoading.value = false
        toast({
          title: t('error'),
          description: t('noDocumentsFetched'),
          variant: 'destructive'
        })
        return
      }

      documents.value = validDocuments

      organizedDocuments.value = await Promise.all(
        validDocuments.map(async (doc, index) => {
          const docThumbnails = documentThumbnails.value.get(doc.uuid) || []
          const pages = await createDocumentPages(doc, index, docThumbnails)

          return {
            name: doc.filename?.replace('.pdf', '') || `Document ${index + 1}`,
            pages: pages
          }
        })
      )

      // Clone document state - exclude heavy thumbnail data
      try {
        // Create a lightweight version without thumbnails for state tracking
        const lightweightDocs = organizedDocuments.value.map((doc: DocumentGroupType) => ({
          name: doc.name,
          pages: doc.pages.map((page: PageItem) => ({
            uuid: page.uuid,
            filename: page.filename,
            pageNumber: page.pageNumber,
            actualPageNumber: page.actualPageNumber,
            documentUuid: page.documentUuid,
            url: page.url
            // Exclude images and thumbnails - they're too large and not needed for change detection
          }))
        }))

        originalDocumentState.value = JSON.parse(JSON.stringify(lightweightDocs))
      } catch (cloneError) {
        console.error('Failed to clone document state:', cloneError)
        // Fallback: minimal clone for structure tracking
        originalDocumentState.value = organizedDocuments.value.map((doc: DocumentGroupType) => ({
          name: doc.name,
          pages: doc.pages.map((page: PageItem) => ({
            uuid: page.uuid,
            pageNumber: page.pageNumber
          }))
        }))
      }

      // Auto-merge if coming from bulk merge (multiple documents from documentIds)
      const bulkMergeDocumentIds = history.state?.documentIds || []
      if (bulkMergeDocumentIds.length > 1 && organizedDocuments.value.length > 1) {
        mergeAllDocuments()
        toast({
          title: t('success'),
          description: t('documentsAutoMerged', { count: bulkMergeDocumentIds.length }),
          variant: 'default'
        })
      }

      isLoading.value = false

      toast({
        title: t('success'),
        description:
          documentIds.length > 0
            ? t('specificDocumentsLoaded', {
                count: validDocuments.length,
                total: documentIds.length
              })
            : t('allDocumentsLoaded', { count: validDocuments.length }),
        variant: 'default'
      })
    } catch (error) {
      console.error('Error in onMounted:', error)
      isLoading.value = false
      toast({
        title: t('error'),
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive'
      })
    }
  })

  const handleFileUploaded = async () => {
    await fetchAllCaseDocuments()
  }

  const handleAddDocumentsConfirm = (data: {
    selectedDocuments: Document[]
    uploadedFiles: any[]
    pages: any[]
  }) => {
    // Add selected/uploaded documents to the documents array so they're tracked properly during save
    data.selectedDocuments.forEach((doc) => {
      if (!documents.value.some((d) => d.uuid === doc.uuid)) {
        documents.value.push(doc)
      }
    })

    if (currentDocumentGroupIndex.value === null) {
      if (data.pages.length === 0) {
        toast({
          title: t('error'),
          description: t('selectDocumentsOrUpload'),
          variant: 'destructive'
        })
        return
      }

      const pagesByDocument = new Map<string, any[]>()

      data.pages.forEach((page) => {
        const docUuid = page.documentUuid
        if (!pagesByDocument.has(docUuid)) {
          pagesByDocument.set(docUuid, [])
        }
        pagesByDocument.get(docUuid)!.push(page)
      })

      // Create document groups for each document
      const newDocumentGroups: DocumentGroupType[] = []
      let totalPagesAdded = 0

      pagesByDocument.forEach((pages, docUuid) => {
        const firstPage = pages[0]
        const documentName = firstPage.filename?.replace('.pdf', '') || t('newDocument')

        // Sort pages by page number to maintain order
        const sortedPages = pages.sort((a, b) => a.pageNumber - b.pageNumber)

        // Recalculate page numbers
        const processedPages = sortedPages.map((page, index) => ({
          ...page,
          pageNumber: index + 1
        }))

        newDocumentGroups.push({
          name: documentName,
          pages: processedPages
        })

        totalPagesAdded += processedPages.length
      })

      // Add all new document groups to organized documents
      organizedDocuments.value.push(...newDocumentGroups)

      // Track documents added
      trackEvent(MetricEventType.PDF_DOCUMENTS_ADDED, {
        document_count: newDocumentGroups.length,
        total_pages: totalPagesAdded,
        from_existing: data.selectedDocuments.length,
        from_upload: data.uploadedFiles.length,
        case_uuid: caseId.value
      })

      currentDocumentGroupIndex.value = null
      currentPageIndex.value = null
      currentSide.value = 'right'
      markAsChanged()

      toast({
        title: t('documentsAdded'),
        description: t('documentGroupsCreated', {
          count: newDocumentGroups.length,
          pages: totalPagesAdded
        }),
        variant: 'default'
      })

      return
    }

    if (currentPageIndex.value === null) {
      toast({
        title: t('error'),
        description: t('noDocumentGroupSelected'),
        variant: 'default'
      })
      return
    }

    const currentGroup = organizedDocuments.value[currentDocumentGroupIndex.value]
    const currentPageIdx = currentPageIndex.value
    const side = currentSide.value
    let addedPages = 0

    if (data.pages.length > 0) {
      const pagesToInsert = data.pages.map((page, index) => ({
        ...page,
        pageNumber: 0
      }))

      let insertIndex: number
      if (side === 'left') {
        insertIndex = currentPageIdx
      } else {
        insertIndex = currentPageIdx + 1
      }

      currentGroup.pages.splice(insertIndex, 0, ...pagesToInsert)
      addedPages = pagesToInsert.length

      for (let i = 0; i < currentGroup.pages.length; i++) {
        currentGroup.pages[i].pageNumber = i + 1
      }
    }

    currentDocumentGroupIndex.value = null
    currentPageIndex.value = null
    currentSide.value = 'right'
    markAsChanged()

    toast({
      title: t('documentsAdded'),
      description: t('pagesAddedToGroup', { count: addedPages, groupName: currentGroup.name }),
      variant: 'default'
    })
  }

  const fetchAllCaseDocuments = async () => {
    try {
      await caseStore.loadCase(caseId.value)

      if (caseStore.documents) {
        allCaseDocuments.value = caseStore.documents
      } else {
        allCaseDocuments.value = []
      }
    } catch (error) {
      console.error('Failed to fetch case documents:', error)
      toast({
        title: t('error'),
        description: t('failedToFetchCaseDocuments'),
        variant: 'destructive'
      })
    }
  }

  const availableDocuments = computed(() => {
    const filtered = allCaseDocuments.value.filter((doc: Document) => {
      if (currentDocumentGroupIndex.value === null) return true

      const currentGroup = organizedDocuments.value[currentDocumentGroupIndex.value]
      return !currentGroup.pages.some((page: PageItem) => page.documentUuid === doc.uuid)
    })

    return filtered
  })

  // Track view closed on unmount
  onUnmounted(() => {
    if (viewOpenedAt.value) {
      const timeSpent = Math.floor((new Date().getTime() - viewOpenedAt.value.getTime()) / 1000)

      trackEvent(MetricEventType.PDF_ORGANIZE_VIEW_CLOSED, {
        case_uuid: caseId.value,
        document_count: organizedDocuments.value.length,
        had_changes: hasChanges.value,
        time_spent_seconds: timeSpent
      })
    }
  })
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <OrganizeDocumentsHeader
      :organized-documents="organizedDocuments"
      :documents="documents"
      :original-document-state="originalDocumentState"
      :case-id="caseId"
      :has-changes="hasChanges"
      :page-rotations="documentOrganization.pageRotations.value"
    />

    <div class="space-y-6 p-6">
      <!-- Loading State -->
      <div
        v-if="isLoading"
        class="flex min-h-[60vh] flex-col items-center justify-center"
      >
        <div class="flex flex-col items-center gap-6">
          <div class="relative h-12 w-12">
            <div
              class="absolute inset-0 animate-[spin_0.8s_linear_infinite] rounded-full border-[3px] border-purple-100
                border-t-purple-600"
            ></div>
          </div>
          <p class="text-lg text-gray-500">{{ t('loadingDocuments') }}</p>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="organizedDocuments.length === 0"
        class="rounded-lg border border-gray-200 bg-white p-4"
      >
        <div class="flex min-h-[15vh] flex-col items-center justify-center">
          <div class="text-center">
            <p class="mb-6 text-lg text-gray-500">{{ t('noDocuments') }}</p>
            <Button
              class="bg-purple-600 px-6 py-3 font-medium text-white hover:bg-purple-700"
              @click="openEmptyStateAddDocuments"
            >
              <Plus class="mr-2 h-5 w-5" />
              {{ t('addDocuments') }}
            </Button>
          </div>
        </div>
      </div>

      <!-- Document Groups -->
      <template v-else>
        <DocumentGroup
          v-for="(document, docIndex) in organizedDocuments"
          :key="docIndex"
          :document="document"
          :document-index="docIndex"
        />
      </template>
    </div>

    <AddDocumentsDialog
      v-model:is-open="showAddDocuments"
      :available-documents="availableDocuments"
      :all-case-documents="allCaseDocuments"
      :case-id="caseId"
      @confirm="handleAddDocumentsConfirm"
      @file-uploaded="handleFileUploaded"
    />

    <!-- Unsaved Document Changes Dialog -->
    <UnsavedChangesDialog
      v-model:open="dialogState.show.value"
      :title="t('changesNotSaved')"
      :description="t('unsavedChangesDescription')"
      :confirm-text="t('discardChanges')"
      :cancel-text="t('goBack')"
      @confirm="dialogState.onConfirm?.value?.()"
      @cancel="dialogState.onCancel?.value?.()"
    />
  </div>
</template>
